import { useState } from 'react';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader, useToast } from '@vd/ui';
import { post } from '../../lib/apiClient';
import type { BillingInterval, BillingPlansResponse } from '@vd/types';
import billingStyles from './Billing.module.css';
import { meterState } from './addonOffers';

/**
 * The widget on the billing page (TOADO-661): this month's reports and
 * visitors' recording minutes against the workspace's allowance (the plan's
 * plus the widget tier's), and the tier. Reports are soft past the allowance:
 * taken without AI titles up to twice it, then the widget pauses until the 1st.
 */

export type WidgetTier = 'starter' | 'growth' | 'scale';

export interface WidgetState {
  tier: WidgetTier | null;
  tierInterval: 'monthly' | 'annual' | null;
  tierStatus: string | null;
  /** The tier the plan comes with (Business: Starter), not bought. */
  tierIncluded?: WidgetTier | null;
  /** The tier bought, while it is live: the only one that can be changed or cancelled. */
  tierBought?: WidgetTier | null;
  /** The tier a subscription is recorded for, in use or not. */
  tierHeld?: WidgetTier | null;
  /** Free with a tier still held: only the tier is shown, to be stopped. */
  tierOnly?: boolean;
  reportsUsed: number;
  reportsAllowance: number | null;
  reportsPauseAt: number | null;
  visitorMinutesUsed: number;
  visitorMinutes: number | null;
  tiers: Array<{ tier: WidgetTier; reports: number; visitorMinutes: number; monthlyCents: number; annualCents: number }>;
}

const TIER_NAMES: Record<WidgetTier, string> = { starter: 'Widget Starter', growth: 'Widget Growth', scale: 'Widget Scale' };
const TIER_ORDER: readonly WidgetTier[] = ['starter', 'growth', 'scale'];
const TIER_STATUS_NAMES: Record<string, string> = { unpaid: 'its payment failed', incomplete: 'its payment did not complete', paused: 'it is paused' };

function dollars(cents: number): string {
  return `$${(cents / 100).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

function Bar({ label, used, limit, unit, note, testId }: { label: string; used: number; limit: number | null; unit: string; note?: string; testId: string }) {
  const state = meterState(used, limit);
  const color = state === 'full' ? 'var(--danger)' : state === 'warn' ? 'var(--warning, #b45309)' : 'var(--accent)';
  const pct = limit ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  return (
    <div data-testid={testId} data-state={state}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 13, marginBottom: note ? 2 : 6 }}>
        <span>{label}</span>
        <span style={{ color: state === 'ok' || state === 'unlimited' ? 'var(--text-secondary)' : color, fontVariantNumeric: 'tabular-nums' }}>
          {limit === null ? `${used.toLocaleString()} ${unit}, unlimited` : `${used.toLocaleString()} of ${limit.toLocaleString()} ${unit}`}
        </span>
      </div>
      {note && <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 6 }}>{note}</div>}
      {limit !== null && (
        <div role="meter" aria-label={`${label} used`} aria-valuemin={0} aria-valuemax={limit} aria-valuenow={Math.min(used, limit)}
          style={{ background: 'var(--bg-muted)', height: 6, borderRadius: 3, overflow: 'hidden' }}>
          <div style={{ width: `${pct}%`, height: '100%', background: color }} />
        </div>
      )}
    </div>
  );
}

/** What a change to the widget tier did, as the server reports it. */
export type TierChangeEffect = 'charged_now' | 'no_credit' | 'ends_at_period_end' | 'cancelled_now' | 'unchanged';

/**
 * What a change will cost, said before the owner confirms it (Codex review
 * round 2): an upgrade is charged now, a downgrade gives no credit and its
 * smaller allowance applies at once, stopping keeps the paid period.
 */
export function tierChangeWarning(
  current: WidgetTier | null,
  next: WidgetTier | null,
  inUse: boolean,
  status: string | null = null,
): { title: string; body: string; confirm: string; effect: TierChangeEffect } {
  const name = (tier: WidgetTier) => TIER_NAMES[tier];
  if (next === null) {
    if (inUse && current && status === 'past_due') {
      return { title: `Stop ${name(current)}?`, body: `Its last payment failed, so it ends now and that payment is not collected.`, confirm: 'End it now', effect: 'cancelled_now' };
    }
    return inUse && current
      ? { title: `Stop ${name(current)}?`, body: `${name(current)} stays on until the end of its current period, then stops. Nothing more is charged for it.`, confirm: 'Stop at period end', effect: 'ends_at_period_end' }
      : { title: 'Cancel this tier?', body: 'It has not been paid for, so it adds nothing. It is cancelled now, and nothing more is charged for it.', confirm: 'Cancel it', effect: 'cancelled_now' };
  }
  const larger = current !== null && TIER_ORDER.indexOf(next) > TIER_ORDER.indexOf(current);
  return larger
    ? {
      title: `Move to ${name(next)}?`,
      body: `The difference for the rest of this period is charged now to the card on file, and ${name(next)}'s reports and visitor minutes apply at once. If the card is declined, the tier stays as it is unless that invoice is paid within a day.`,
      confirm: 'Move and pay now',
      effect: 'charged_now',
    }
    : {
      title: `Move to ${name(next)}?`,
      body: `This takes effect now, with no credit for the rest of this period: ${name(next)}'s smaller allowance applies at once. Reports past it this month come in without AI titles, and past twice it the widget pauses until the 1st.`,
      confirm: `Move to ${TIER_NAMES[next]}`,
      effect: 'no_credit',
    };
}

const EFFECT_TOASTS: Record<TierChangeEffect, (tier: WidgetTier | null) => { title: string; description: string }> = {
  charged_now: (tier) => ({ title: tier ? `Moved to ${TIER_NAMES[tier]}` : 'Tier changed', description: 'The difference for this period was charged to the card on file.' }),
  no_credit: (tier) => ({ title: tier ? `Moved to ${TIER_NAMES[tier]}` : 'Tier changed', description: 'Its allowance applies now. The next renewal is at its price.' }),
  ends_at_period_end: () => ({ title: 'The tier ends with its period', description: 'Until then it stays on.' }),
  cancelled_now: () => ({ title: 'The tier is cancelled', description: 'Nothing more is charged for it.' }),
  unchanged: () => ({ title: 'Nothing to change', description: 'That is the tier already on.' }),
};

export function WidgetUsage({ widget, isOwner, companyId, billingConfigured, onChanged, presentation = 'standard', interval = 'annual', displayTiers }: {
  widget: WidgetState;
  isOwner: boolean;
  companyId: string;
  billingConfigured: boolean;
  onChanged: () => void;
  presentation?: 'standard' | 'plans';
  interval?: BillingInterval;
  displayTiers?: BillingPlansResponse['widgetTiers'];
}) {
  const toast = useToast();
  const [busy, setBusy] = useState<string | null>(null);
  /** The change waiting for the owner to confirm what it costs. */
  const [confirming, setConfirming] = useState<{ tier: WidgetTier | null } | null>(null);
  const reportsNote = widget.reportsAllowance !== null && widget.reportsPauseAt !== null
    ? widget.reportsUsed >= widget.reportsPauseAt
      ? 'The widget is paused until the 1st: it is past twice its reports.'
      : widget.reportsUsed >= widget.reportsAllowance
        ? `Past ${widget.reportsAllowance.toLocaleString()}, reports come in without AI titles; at ${widget.reportsPauseAt.toLocaleString()} the widget pauses until the 1st.`
        : undefined
    : undefined;

  // A tier the plan includes (Business: Starter) is not bought or changed;
  // only a larger one is offered, and replaces it (TOADO-662).
  const included = widget.tierIncluded ?? null;
  const bought = widget.tierBought === undefined ? widget.tier : widget.tierBought;
  const buyable = widget.tiers.filter((option) => !included || TIER_ORDER.indexOf(option.tier) > TIER_ORDER.indexOf(included));

  const devMode = () => toast.push({ variant: 'info', title: 'Billing not configured (dev mode)', description: 'Set the STRIPE_* variables on the api to buy a widget tier.' });

  const buy = async (tier: WidgetTier, interval: 'monthly' | 'annual') => {
    if (!billingConfigured) { devMode(); return; }
    setBusy(`${tier}-${interval}`);
    try {
      const { url } = await post<{ url: string }>('/v1/billing/widget-tier/checkout', { companyId, tier, interval });
      window.location.assign(url);
    } catch (error) {
      toast.push({ variant: 'danger', title: 'Could not start checkout', description: (error as Error).message });
      setBusy(null);
    }
  };

  const ask = (tier: WidgetTier | null) => {
    if (!billingConfigured) { devMode(); return; }
    setConfirming({ tier });
  };

  const change = async (tier: WidgetTier | null, expect: TierChangeEffect) => {
    setConfirming(null);
    setBusy(tier ?? 'stop');
    try {
      // `expect` is what the owner was told: the server refuses if Stripe would now do otherwise.
      const result = await post<{ tier: WidgetTier | null; effect: TierChangeEffect }>('/v1/billing/widget-tier/change', { companyId, tier, expect });
      toast.push({ variant: 'success', ...EFFECT_TOASTS[result.effect](result.tier ?? tier) });
      onChanged();
    } catch (error) {
      toast.push({ variant: 'danger', title: 'Could not change the tier', description: (error as Error).message });
    } finally {
      setBusy(null);
    }
  };

  // Against the tier bought, not one the plan includes, which is not changed here.
  const warning = confirming ? tierChangeWarning(bought ?? widget.tierHeld ?? null, confirming.tier, !!bought, widget.tierStatus) : null;

  const confirmation = confirming && warning && (
    <Modal
      open
      onClose={() => setConfirming(null)}
      width={440}
      labelledBy="widget-tier-confirm-title"
    >
      <ModalHeader id="widget-tier-confirm-title">{warning.title}</ModalHeader>
      <ModalBody>
        <p data-testid="widget-tier-confirm-body" style={{ margin: 0 }}>
          {warning.body}
        </p>
      </ModalBody>
      <ModalFooter>
        <Button variant="secondary" onClick={() => setConfirming(null)}>
          Keep as is
        </Button>
        <Button
          variant="primary"
          onClick={() => {
            void change(confirming.tier, warning.effect);
          }}
        >
          {warning.confirm}
        </Button>
      </ModalFooter>
    </Modal>
  );
  if (presentation === 'plans') {
    const existing = bought ?? widget.tierHeld;
    const offerInterval = existing ? (widget.tierInterval ?? interval) : interval;
    const offers = displayTiers ?? widget.tiers.map((tier) => ({ ...tier, available: true }));
    return (
      <section data-testid="widget-capacity-plans">
        {widget.tierOnly && (
          <p className={billingStyles.footnote}>
            This widget subscription adds no capacity on Free and stops renewing when its period
            ends.
          </p>
        )}
        {included && (
          <p className={billingStyles.footnote}>
            {TIER_NAMES[included]} is included with this plan. A larger purchased tier replaces it.
          </p>
        )}
        {existing && (
          <div className={billingStyles.spread}>
            <p className={billingStyles.muted}>
              {TIER_NAMES[existing]}
              {widget.tierInterval ? `, ${widget.tierInterval}` : ''}
              {!bought ? ' (not in use)' : ''}. Tier changes retain the existing interval. Prices
              below are current catalog offers, not your renewal price.
            </p>
            {isOwner && (
              <Button variant="secondary" disabled={busy !== null} onClick={() => ask(null)}>
                {busy === 'stop' ? 'Saving…' : bought ? 'Stop widget tier' : 'Cancel inactive tier'}
              </Button>
            )}
          </div>
        )}
        <div className={billingStyles.widgetGrid}>
          {offers.map((option) => {
            const isIncluded = option.tier === included;
            const isCurrent = option.tier === bought;
            const heldInactive = !!widget.tierHeld && !bought;
            const canBuy =
              option.available &&
              buyable.some((tier) => tier.tier === option.tier) &&
              !isCurrent &&
              !heldInactive;
            return (
              <article
                className={`${billingStyles.tierCard} ${isCurrent ? billingStyles.current : ''}`}
                key={option.tier}
              >
                <h4>{TIER_NAMES[option.tier]}</h4>
                {isIncluded && <span className={billingStyles.badge}>Included</span>}
                {isCurrent && <span className={billingStyles.badge}>Current tier</span>}
                <p>
                  {option.reports.toLocaleString()} reports / month
                  <br />
                  {option.visitorMinutes.toLocaleString()} visitor minutes / month
                </p>
                <div>
                  <strong>
                    {dollars(offerInterval === 'annual' ? option.annualCents : option.monthlyCents)}
                  </strong>
                  <span className={billingStyles.muted}>
                    {' '}
                    / {offerInterval === 'annual' ? 'year' : 'month'}, per workspace
                  </span>
                </div>
                <p>
                  {included
                    ? 'A larger tier replaces included capacity.'
                    : 'Added to your plan’s included widget capacity.'}
                </p>
                {isOwner ? (
                  <Button
                    variant="secondary"
                    disabled={busy !== null || !canBuy || widget.tierOnly}
                    onClick={() =>
                      bought ? ask(option.tier) : void buy(option.tier, offerInterval)
                    }
                  >
                    {isCurrent
                      ? 'Current tier'
                      : isIncluded
                        ? 'Included with your plan'
                        : heldInactive
                          ? 'Cancel inactive tier first'
                          : !canBuy
                            ? 'Unavailable'
                            : bought
                              ? `Move to ${TIER_NAMES[option.tier]}`
                              : `Choose ${TIER_NAMES[option.tier]}`}
                  </Button>
                ) : (
                  <p className={billingStyles.muted}>Only the owner can change widget capacity.</p>
                )}
              </article>
            );
          })}
        </div>
        {confirmation}
      </section>
    );
  }

  return (
    <section data-testid="widget-usage" style={{ display: 'grid', gap: 12 }}>
      <h3 style={{ fontSize: 14, margin: 0 }}>{widget.tierOnly ? 'Widget tier' : 'Widget this month'}</h3>
      {widget.tierOnly ? (
        <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }} data-testid="widget-tier-on-free">
          Widget tiers are for paid plans: this one adds nothing on Free, and stops renewing at the end of its period.
        </div>
      ) : (
        <>
          <Bar label="Reports" used={widget.reportsUsed} limit={widget.reportsAllowance} unit="reports" note={reportsNote} testId="widget-meter-reports" />
          <Bar label="Visitor recording" used={widget.visitorMinutesUsed} limit={widget.visitorMinutes} unit="minutes"
            note={widget.visitorMinutes !== null && widget.visitorMinutesUsed >= widget.visitorMinutes ? 'Visitors are not offered a recording until the 1st; screenshots and written reports go on.' : undefined}
            testId="widget-meter-visitors" />
        </>
      )}
      {!bought && widget.tierHeld ? (
        // Held but not in use: it adds nothing, and the only thing to do is
        // stop it. Nothing can be bought until it is stopped.
        <>
          {included && (
            <div style={{ fontSize: 13 }} data-testid="widget-tier-included">{TIER_NAMES[included]}, included with your plan</div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, fontSize: 13, flexWrap: 'wrap' }} data-testid="widget-tier-held">
            <span>{TIER_NAMES[widget.tierHeld]} is not in use: {TIER_STATUS_NAMES[widget.tierStatus ?? ''] ?? 'it is not active'}.</span>
            {isOwner && (
              <Button variant="secondary" size="sm" disabled={busy !== null} onClick={() => ask(null)}>
                {busy === 'stop' ? 'Saving…' : 'Cancel it'}
              </Button>
            )}
          </div>
        </>
      ) : bought ? (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, fontSize: 13, flexWrap: 'wrap' }} data-testid="widget-tier-current">
          <span>{TIER_NAMES[bought]}{widget.tierInterval ? `, ${widget.tierInterval}` : ''}</span>
          {isOwner && (
            <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {buyable.filter((option) => option.tier !== bought).map((option) => (
                <Button key={option.tier} variant="secondary" size="sm" disabled={busy !== null} onClick={() => ask(option.tier)}>
                  {busy === option.tier ? 'Saving…' : `Move to ${TIER_NAMES[option.tier]}`}
                </Button>
              ))}
              <Button variant="secondary" size="sm" disabled={busy !== null} onClick={() => ask(null)}>
                {busy === 'stop' ? 'Saving…' : 'Cancel'}
              </Button>
            </span>
          )}
        </div>
      ) : (
        <>
          {included && (
            <div style={{ fontSize: 13 }} data-testid="widget-tier-included">{TIER_NAMES[included]}, included with your plan</div>
          )}
          {isOwner && buyable.length > 0 && (
            <div style={{ display: 'grid', gap: 8 }} data-testid="widget-tier-offers">
              <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
                {included ? 'A larger tier replaces it' : 'A widget tier adds reports and visitor minutes every month'}.
                {buyable.every((option) => option.annualCents === option.monthlyCents * 10) ? ' Yearly is two months free.' : ''}
              </div>
              {buyable.map((option) => (
                <div key={option.tier} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, fontSize: 13, flexWrap: 'wrap' }}>
                  <span>
                    <strong>{TIER_NAMES[option.tier]}</strong>
                    <span style={{ color: 'var(--text-secondary)' }}> · {option.reports.toLocaleString()} reports, {option.visitorMinutes.toLocaleString()} visitor minutes</span>
                  </span>
                  <span style={{ display: 'flex', gap: 6 }}>
                    <Button variant="secondary" size="sm" disabled={busy !== null} onClick={() => { void buy(option.tier, 'monthly'); }}>
                      {busy === `${option.tier}-monthly` ? 'Redirecting…' : `${dollars(option.monthlyCents)} / mo`}
                    </Button>
                    <Button variant="secondary" size="sm" disabled={busy !== null} onClick={() => { void buy(option.tier, 'annual'); }}>
                      {busy === `${option.tier}-annual` ? 'Redirecting…' : `${dollars(option.annualCents)} / yr`}
                    </Button>
                  </span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
      {confirmation}
    </section>
  );
}
